import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@sho-bot/ui';
import type { ReactNode } from 'react';
import { API_ERROR_LABELS, type ApiErrorCode } from '../../shared/errors.js';
import { ApiRequestError } from '../api/client.js';

/**
 * Loading, empty, and failure states.
 *
 * Written once so every page says the same thing in the same shape. The
 * distinction that matters here is between *empty* and *broken*: a server with
 * no archived messages yet and a server whose query failed look identical if
 * both render as a blank card, and only one of them is a problem.
 *
 * No error state ever renders an exception message. Everything the browser
 * receives is one of a closed set of codes with a fixed sentence attached, so a
 * connection URL or a stack frame has no path onto the page.
 */

export function ErrorState({
  error,
  onRetry,
  title = 'Unable to load',
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}): ReactNode {
  const code: ApiErrorCode = error instanceof ApiRequestError ? error.code : 'internal_error';
  return (
    <Alert variant="destructive" role="alert">
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        {API_ERROR_LABELS[code]}
        {onRetry !== undefined ? (
          <span className="mt-2 block">
            <Button size="sm" variant="outline" onClick={onRetry}>
              Try again
            </Button>
          </span>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

export function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}): ReactNode {
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

export function NoServerSelected(): ReactNode {
  return <EmptyState title="No server selected" description="Choose a server in the sidebar." />;
}

/**
 * An in-section empty, for a table or list that has nothing to show.
 *
 * Distinct from `EmptyState`, which is a whole-page composition. A muted
 * sentence in the existing column is the right weight when the rest of the
 * page is already on screen.
 */
export function QuietEmpty({ children }: { children: ReactNode }): ReactNode {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

/**
 * A partial failure.
 *
 * Used when part of a page loaded and part did not — the archive answered but
 * the bot did not, say. It is a warning rather than an error because what is on
 * screen is still true; it is just not the whole picture, and saying so is the
 * difference between an honest page and a misleading one.
 */
export function PartialFailureNotice({ children }: { children: ReactNode }): ReactNode {
  return (
    <Alert role="status">
      <AlertTitle>Some data is missing</AlertTitle>
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}
